import React, { forwardRef } from "react";

import { Box, Stack, StackProps, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, getDollarValue, formatNarrowCurrencyAmount } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

type FormPaymentOrderSectionProps = Omit<StackProps, "title"> & {
  title: React.ReactNode;
  subtotal: number;
  currency: AvailablePaymentCurrency;
  recurrence?: "monthly" | "yearly";
};

export const FormPaymentOrderSection = forwardRef<HTMLDivElement, FormPaymentOrderSectionProps>(
  ({ children, title, subtotal, currency, recurrence, ...rest }, ref) => {
    const { isoLocale } = useLocaleContext();
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentSummary) {
      return <InvalidThemeErrorMessage />;
    }

    return (
      <Stack ref={ref} gap={1} {...rest}>
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <FormTypography variant="subtitle1">{title}</FormTypography>
          <FormTypography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
            {formatNarrowCurrencyAmount(getDollarValue(subtotal), { withCents: true, locale: isoLocale, currency })}
            {recurrence && (
              <Box display="inline-block" marginInlineStart={0.5}>
                {recurrence === "monthly" && "/month"}
                {recurrence === "yearly" && "/year"}
              </Box>
            )}
          </FormTypography>
        </Stack>
        {children}
      </Stack>
    );
  }
);

FormPaymentOrderSection.displayName = "FormPaymentOrderSection";
